import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Hourglass } from "lucide-react";
import { toast } from "sonner";
import { AlertBar } from "@/components/AlertBar";
import { AuthLayout } from "@/components/catalyst/AuthLayout";
import { Button } from "@/components/catalyst/Button";
import { Heading } from "@/components/catalyst/Heading";
import { Select } from "@/components/catalyst/Select";
import { Logo } from "@/components/Logo";
import { StripePaymentForm } from "@/components/stripe-payment-form";
import { orpc } from "@/orpc/client";
import { waitForSubscriptionActive } from "@/pages/checkout/wait-for-subscription";
import { Route } from "@/routes/checkout";

export function CheckoutPage() {
	const { plan, billingPeriod } = Route.useSearch();
	const navigate = useNavigate();

	const { data, isError, error } = useSuspenseQuery({
		...orpc.billing.createEmbeddedCheckoutSession.queryOptions({
			input: { plan, billingPeriod },
		}),
		retry: 1,
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	if (isError || !data.clientSecret) {
		if (error?.message) {
			toast.error(error.message, { id: "checkout-error" });
		}
		return (
			<AuthLayout>
				<div className="text-error">
					Failed to initialize checkout. Please refresh or contact support if
					this issue persists.
				</div>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout>
			<div className="flex flex-col w-full max-w-sm">
				<div className="flex flex-col items-center gap-4 text-center">
					<Logo />
					<div>
						<Heading>Complete Your Subscription</Heading>
						<div className="text-base-content/60 mt-2 text-sm">
							<span className="flex items-center justify-center gap-1.5 flex-wrap">
								You're subscribing to the
								<Select
									value={billingPeriod}
									showArrow={false}
									className="w-auto! inline-flex [&>select]:py-1 [&>select]:px-2 [&>select]:text-xs [&>select]:font-semibold [&>select]:text-base-content [&>select]:text-center"
									onChange={(e) =>
										navigate({
											to: "/checkout",
											search: {
												plan,
												billingPeriod: e.target.value as "monthly" | "yearly",
											},
										})
									}
								>
									<option value="monthly">Monthly</option>
									<option value="yearly">Annual</option>
								</Select>
								<Select
									value={plan}
									showArrow={false}
									className="w-auto! inline-flex [&>select]:py-1 [&>select]:px-2 [&>select]:text-xs [&>select]:font-semibold [&>select]:text-base-content [&>select]:text-center"
									onChange={(e) =>
										navigate({
											to: "/checkout",
											search: {
												plan: e.target.value as
													| "bootstrap"
													| "standard"
													| "scale",
												billingPeriod,
											},
										})
									}
								>
									<option value="bootstrap">Bootstrap</option>
									<option value="standard">Standard</option>
									<option value="scale">Scale</option>
								</Select>
								plan
							</span>
							{plan === "standard" && (
								<AlertBar
									variant="default"
									className="mt-4 mb-0!"
									icon={Hourglass}
								>
									Includes a 14-day free trial. You won't be charged until the
									trial ends.
								</AlertBar>
							)}
						</div>
					</div>
				</div>

				<StripePaymentForm
					clientSecret={data.clientSecret}
					className="mt-8 space-y-6"
					onSubmit={async ({ stripe, elements }) => {
						// For trial subscriptions, Stripe uses a SetupIntent to collect card details
						// without charging. For non-trial, a PaymentIntent collects payment immediately.
						// redirect: "if_required" keeps us on the page when no redirect is needed (most cards),
						// falling back to return_url only for 3DS/bank redirects.
						const result = data.isSetupIntent
							? await stripe.confirmSetup({
									elements,
									confirmParams: {
										return_url: `${window.location.origin}/checkout-return`,
									},
									redirect: "if_required",
								})
							: await stripe.confirmPayment({
									elements,
									confirmParams: {
										return_url: `${window.location.origin}/checkout-return`,
									},
									redirect: "if_required",
								});

						if (result.error) {
							return result.error.message;
						}

						// Payment succeeded without redirect — wait for the Stripe webhook
						// to update subscription status before navigating to dashboard.
						const active = await waitForSubscriptionActive();
						if (active) {
							navigate({ to: "/dashboard" });
						} else {
							return "Payment received but activation is taking longer than expected. Please refresh and try again.";
						}
					}}
				>
					{({ paymentElement, error, isProcessing, isReady }) => (
						<>
							{paymentElement}
							{error}
							<div className="flex gap-3">
								<Button
									type="button"
									outline
									className="flex-1"
									onClick={() => navigate({ to: "/auth/signin" })}
									disabled={isProcessing}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									color="dark/white"
									className="flex-1"
									disabled={!isReady || isProcessing}
								>
									{isProcessing ? "Processing..." : "Subscribe"}
								</Button>
							</div>
						</>
					)}
				</StripePaymentForm>
			</div>
		</AuthLayout>
	);
}
